import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/App';
import { useAnalytics } from "../providers/AnalyticsProvider";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  BarChart3,
  TrendingUp,
  Activity,
  Clock,
  Eye,
  MousePointer,
  RefreshCw,
  Crown,
  Lock,
  ArrowUpRight,
  Users,
  Calendar,
  Zap,
  Sparkles,
  Target,
  ChevronUp,
  ChevronDown,
  Minus,
  Brain,
  AlertTriangle,
  DollarSign,
  ShoppingBag,
  Wallet,
  FileText,
  Download
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsDashboard = () => {
  const { user } = useAuth();
  const { eventCount, lastEvent, isInitialized, isPostHogConfigured } = useAnalytics();
  const navigate = useNavigate();
  
  const [analyticsStatus, setAnalyticsStatus] = useState(null);
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [trendProfile, setTrendProfile] = useState(null);
  const [potentialEarnings, setPotentialEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const isPro = user?.user_tier === 'pro' || user?.is_premium;

  const fetchAnalyticsData = useCallback(async (showRefreshSpinner = false) => {
    if (showRefreshSpinner) setRefreshing(true);
    else setLoading(true);
    
    setError(null);
    const token = localStorage.getItem('token');

    try {
      // Always fetch status (available to all users)
      const statusRes = await axios.get(`${API}/analytics/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalyticsStatus(statusRes.data);

      // Only fetch advanced analytics for pro users
      if (isPro) {
        // Fetch all pro data in parallel (including earnings)
        const [summaryRes, predictionsRes, profileRes, earningsRes] = await Promise.allSettled([
          axios.get(`${API}/analytics/summary?days=7`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/analytics/predictions`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/user/trend-profile`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/affiliate/earnings?days=30`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        if (summaryRes.status === 'fulfilled') {
          setAnalyticsSummary(summaryRes.value.data);
        }
        if (predictionsRes.status === 'fulfilled') {
          setPredictions(predictionsRes.value.data);
        }
        if (profileRes.status === 'fulfilled') {
          setTrendProfile(profileRes.value.data);
        }
        if (earningsRes.status === 'fulfilled') {
          setPotentialEarnings(earningsRes.value.data);
        }
      }
    } catch (err) {
      console.error('Analytics fetch error:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isPro]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  // Auto-refresh every 30 seconds for live data
  useEffect(() => {
    const interval = setInterval(() => {
      fetchAnalyticsData(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchAnalyticsData]);

  const handleRefresh = () => {
    fetchAnalyticsData(true);
  };

  const handleExportTrends = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/export/trends?format=csv&limit=50`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trendi_analysis_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Trend analysis exported successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export trends");
    }
  };

  const formatTimeAgo = (isoString) => {
    if (!isoString) return 'Never';
    const date = new Date(isoString);
    const now = new Date();
    const diffSeconds = Math.floor((now - date) / 1000);
    
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
    if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
    return `${Math.floor(diffSeconds / 86400)}d ago`;
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'page_view': return <Eye className="w-4 h-4" />;
      case 'click': return <MousePointer className="w-4 h-4" />;
      default: return <Activity className="w-4 h-4" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'page_view': return 'bg-blue-100 text-blue-700';
      case 'click': return 'bg-green-100 text-green-700';
      case 'search': return 'bg-purple-100 text-purple-700';
      case 'save': return 'bg-yellow-100 text-yellow-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-black border-t-transparent animate-spin mx-auto mb-4"></div>
          <p className="font-mono text-sm text-gray-500">Loading analytics...</p>
        </div>
      </div>
    );
  }

  // FREE TIER TEASER VIEW
  if (!isPro) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-6 h-6" />
              <h1 className="text-2xl font-bold">Analytics Dashboard</h1>
              <Badge variant="outline" className="ml-2">
                <Lock className="w-3 h-3 mr-1" />
                Pro Feature
              </Badge>
            </div>
            <p className="text-gray-600">Unlock powerful insights about user engagement and trends</p>
          </div>

          {/* Free Tier Preview */}
          <Card className="mb-6 border-2 border-dashed border-gray-300">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-green-500" />
                Basic Analytics Status
              </CardTitle>
              <CardDescription>Available on Free tier</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-800">{analyticsStatus?.total_interactions || 0}</div>
                  <div className="text-xs text-gray-500">Total Events</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-800">{analyticsStatus?.today_events || 0}</div>
                  <div className="text-xs text-gray-500">Today&apos;s Events</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-gray-800">{analyticsStatus?.last_hour_count || 0}</div>
                  <div className="text-xs text-gray-500">Last Hour</div>
                </div>
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-center gap-1">
                    <span className={`w-2 h-2 rounded-full ${analyticsStatus?.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                    <span className="text-sm font-medium">{analyticsStatus?.status === 'active' ? 'Active' : 'No Data'}</span>
                  </div>
                  <div className="text-xs text-gray-500">Status</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upgrade CTA */}
          <Card className="bg-gradient-to-br from-black to-gray-800 text-white overflow-hidden">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="text-center md:text-left">
                  <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
                    <Crown className="w-8 h-8 text-yellow-400" />
                    <span className="text-xl font-bold">Upgrade to Pro</span>
                  </div>
                  <p className="text-gray-300 mb-4 max-w-md">
                    Get access to detailed analytics, user engagement metrics, daily trends, and actionable insights to grow your business.
                  </p>
                  
                  {/* Feature Preview */}
                  <div className="grid grid-cols-2 gap-3 text-sm mb-6">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4 text-green-400" />
                      <span>Weekly trend reports</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-blue-400" />
                      <span>Active user tracking</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-purple-400" />
                      <span>Daily event breakdown</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-yellow-400" />
                      <span>Real-time data stream</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <Button 
                    size="lg" 
                    className="bg-white text-black hover:bg-gray-100 font-semibold"
                    onClick={() => navigate('/payment')}
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Upgrade Now
                    <ArrowUpRight className="w-4 h-4 ml-2" />
                  </Button>
                  <p className="text-xs text-gray-400 text-center">Starting at $29.99/month</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Blurred Preview of Pro Features */}
          <div className="mt-8 relative">
            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
              <div className="text-center">
                <Lock className="w-10 h-10 mx-auto mb-2 text-gray-400" />
                <p className="text-gray-600 font-medium">Pro Analytics Preview</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 opacity-50">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Events by Action</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {['page_view', 'click', 'search'].map(action => (
                      <div key={action} className="flex justify-between items-center">
                        <span className="text-sm capitalize">{action.replace('_', ' ')}</span>
                        <div className="w-32 h-2 bg-gray-200 rounded-full"></div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Events by Category</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {['Home', 'Explore', 'Trend Detail'].map(cat => (
                      <div key={cat} className="flex justify-between items-center">
                        <span className="text-sm">{cat}</span>
                        <div className="w-32 h-2 bg-gray-200 rounded-full"></div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // PRO TIER FULL DASHBOARD
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <BarChart3 className="w-6 h-6" />
              <h1 className="text-2xl font-bold">Analytics Dashboard</h1>
              <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white">
                <Crown className="w-3 h-3 mr-1" />
                Pro
              </Badge>
            </div>
            <p className="text-gray-600">Real-time user engagement and interaction data</p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleRefresh}
              disabled={refreshing}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            
            <Button
              onClick={handleExportTrends}
              className="gap-2 bg-gradient-to-r from-[#FF0099] to-[#FF6B6B] text-white hover:shadow-lg"
            >
              <FileText className="w-4 h-4" />
              Export Trends
            </Button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        {/* Live Status Indicator */}
        <Card className="mb-6 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <div className="absolute inset-0 w-3 h-3 bg-green-500 rounded-full animate-ping"></div>
                </div>
                <div>
                  <span className="font-semibold text-green-800">Live Tracking Active</span>
                  <p className="text-xs text-green-600">
                    PostHog: {isPostHogConfigured ? 'Connected' : 'Not Configured'} • 
                    Session Events: {eventCount}
                  </p>
                </div>
              </div>
              {lastEvent && (
                <div className="text-right">
                  <span className="text-xs text-green-600">Last Event</span>
                  <p className="text-sm font-mono text-green-800">{lastEvent.name}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Events</p>
                  <p className="text-2xl font-bold">{analyticsStatus?.total_interactions || 0}</p>
                </div>
                <Activity className="w-8 h-8 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Today</p>
                  <p className="text-2xl font-bold">{analyticsStatus?.today_events || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Last Hour</p>
                  <p className="text-2xl font-bold">{analyticsStatus?.last_hour_count || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Users (7d)</p>
                  <p className="text-2xl font-bold">{analyticsSummary?.active_users || 0}</p>
                </div>
                <Users className="w-8 h-8 text-orange-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Analytics */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Events by Action */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <MousePointer className="w-5 h-5" />
                Events by Action (7 days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analyticsSummary?.by_action && Object.keys(analyticsSummary.by_action).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(analyticsSummary.by_action)
                    .sort(([,a], [,b]) => b - a)
                    .map(([action, count]) => {
                      const total = Object.values(analyticsSummary.by_action).reduce((a, b) => a + b, 0);
                      const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                      
                      return (
                        <div key={action} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span className="capitalize flex items-center gap-2">
                              {getActionIcon(action)}
                              {action.replace('_', ' ')}
                            </span>
                            <span className="font-medium">{count} ({percentage}%)</span>
                          </div>
                          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-black rounded-full transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">No action data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Events by Category */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Events by Category (7 days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {analyticsSummary?.by_category && Object.keys(analyticsSummary.by_category).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(analyticsSummary.by_category)
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 8)
                    .map(([category, count]) => {
                      const total = Object.values(analyticsSummary.by_category).reduce((a, b) => a + b, 0);
                      const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                      
                      return (
                        <div key={category} className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span>{category}</span>
                            <span className="font-medium">{count} ({percentage}%)</span>
                          </div>
                          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              ) : (
                <p className="text-gray-500 text-sm text-center py-4">No category data yet</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Events Feed */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5" />
              Recent Events Stream
            </CardTitle>
            <CardDescription>Last 5 tracked interactions</CardDescription>
          </CardHeader>
          <CardContent>
            {analyticsStatus?.recent_events && analyticsStatus.recent_events.length > 0 ? (
              <div className="space-y-3">
                {analyticsStatus.recent_events.map((event, idx) => (
                  <div 
                    key={event.id || idx} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${getActionColor(event.action)}`}>
                        {getActionIcon(event.action)}
                      </div>
                      <div>
                        <p className="font-medium text-sm capitalize">{event.action.replace('_', ' ')}</p>
                        <p className="text-xs text-gray-500">{event.category}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="outline" className="text-xs">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatTimeAgo(event.time)}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Activity className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">No recent events</p>
                <p className="text-xs text-gray-400 mt-1">Events will appear here as users interact with the app</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Daily Trend Chart (Simplified) */}
        {analyticsSummary?.daily_counts && analyticsSummary.daily_counts.length > 0 && (
          <Card className="mt-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Daily Event Volume (Last 7 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end justify-between h-32 gap-2">
                {analyticsSummary.daily_counts.map((day, idx) => {
                  const maxCount = Math.max(...analyticsSummary.daily_counts.map(d => d.count));
                  const heightPercent = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                  
                  return (
                    <div key={day.date} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-xs font-medium">{day.count}</span>
                      <div 
                        className="w-full bg-gradient-to-t from-black to-gray-600 rounded-t transition-all duration-500"
                        style={{ height: `${Math.max(heightPercent, 5)}%` }}
                      />
                      <span className="text-[10px] text-gray-500">
                        {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Predictive Trends Section */}
        <Card className="mt-6 border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-600" />
                Predictive Trends
                <Badge className="bg-purple-600 text-white text-xs">
                  <Sparkles className="w-3 h-3 mr-1" />
                  AI Powered
                </Badge>
              </CardTitle>
              {predictions?.generated_at && (
                <span className="text-xs text-gray-500">
                  Updated {formatTimeAgo(predictions.generated_at)}
                </span>
              )}
            </div>
            <CardDescription>
              Categories predicted to spike for you next week based on your habits
            </CardDescription>
          </CardHeader>
          <CardContent>
            {predictions?.predictions && predictions.predictions.length > 0 ? (
              <div className="space-y-4">
                {/* Summary Stats */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                    <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
                      <ChevronUp className="w-4 h-4" />
                      <span className="text-xl font-bold">{predictions.summary?.spike_categories || 0}</span>
                    </div>
                    <div className="text-xs text-gray-500">Predicted Spikes</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                    <div className="flex items-center justify-center gap-1 text-blue-600 mb-1">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-xl font-bold">{predictions.summary?.increasing_categories || 0}</span>
                    </div>
                    <div className="text-xs text-gray-500">Increasing</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                    <div className="flex items-center justify-center gap-1 text-orange-600 mb-1">
                      <Target className="w-4 h-4" />
                      <span className="text-xl font-bold">{predictions.data_points_analyzed || 0}</span>
                    </div>
                    <div className="text-xs text-gray-500">Data Points</div>
                  </div>
                </div>

                {/* Spike Alerts */}
                {predictions.spike_alerts && predictions.spike_alerts.length > 0 && (
                  <div className="mb-4 p-3 bg-gradient-to-r from-green-100 to-emerald-100 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-4 h-4 text-green-600" />
                      <span className="font-semibold text-green-800 text-sm">Spike Alerts</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {predictions.spike_alerts.map((alert, idx) => (
                        <Badge key={idx} className="bg-green-600 text-white">
                          {alert.category} +{alert.predicted_change_percent?.toFixed(0)}%
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Predictions List */}
                <div className="space-y-2">
                  {predictions.predictions.slice(0, 6).map((pred, idx) => {
                    const getPredictionIcon = (type) => {
                      switch(type) {
                        case 'spike': return <ChevronUp className="w-4 h-4 text-green-500" />;
                        case 'increase': return <TrendingUp className="w-4 h-4 text-blue-500" />;
                        case 'decline': return <ChevronDown className="w-4 h-4 text-red-500" />;
                        default: return <Minus className="w-4 h-4 text-gray-400" />;
                      }
                    };
                    
                    const getPredictionColor = (type) => {
                      switch(type) {
                        case 'spike': return 'bg-green-100 border-green-300';
                        case 'increase': return 'bg-blue-50 border-blue-200';
                        case 'decline': return 'bg-red-50 border-red-200';
                        default: return 'bg-gray-50 border-gray-200';
                      }
                    };

                    const getConfidenceColor = (conf) => {
                      switch(conf) {
                        case 'high': return 'bg-green-500';
                        case 'medium': return 'bg-yellow-500';
                        default: return 'bg-gray-400';
                      }
                    };

                    return (
                      <div 
                        key={pred.category} 
                        className={`p-3 rounded-lg border ${getPredictionColor(pred.prediction_type)} flex items-center justify-between`}
                      >
                        <div className="flex items-center gap-3">
                          {getPredictionIcon(pred.prediction_type)}
                          <div>
                            <div className="font-medium text-sm">{pred.category}</div>
                            <div className="text-xs text-gray-500">{pred.reasoning}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-sm font-bold ${
                            pred.predicted_change_percent > 0 ? 'text-green-600' : 
                            pred.predicted_change_percent < 0 ? 'text-red-600' : 'text-gray-600'
                          }`}>
                            {pred.predicted_change_percent > 0 ? '+' : ''}{pred.predicted_change_percent?.toFixed(0)}%
                          </div>
                          <div className="flex items-center gap-1 justify-end">
                            <span className={`w-2 h-2 rounded-full ${getConfidenceColor(pred.confidence)}`}></span>
                            <span className="text-xs text-gray-500 capitalize">{pred.confidence}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Recommended Focus */}
                {predictions.recommended_focus && predictions.recommended_focus.length > 0 && (
                  <div className="mt-4 p-3 bg-white rounded-lg border">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-purple-600" />
                      <span className="font-semibold text-sm">Recommended Focus</span>
                    </div>
                    <div className="space-y-2">
                      {predictions.recommended_focus.map((focus, idx) => (
                        <div key={idx} className="flex items-center justify-between text-sm">
                          <span className="font-medium">{focus.category}</span>
                          <Badge variant="outline" className="text-xs">
                            {focus.reason}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : predictions?.message ? (
              <div className="text-center py-8">
                <Brain className="w-12 h-12 mx-auto text-purple-300 mb-3" />
                <p className="text-purple-700 font-medium">{predictions.message}</p>
                <p className="text-xs text-purple-500 mt-1">
                  Explore more trends to unlock personalized predictions
                </p>
              </div>
            ) : (
              <div className="text-center py-8">
                <Brain className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-500">Loading predictions...</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Trend Profile Summary */}
        {trendProfile && trendProfile.categories && (
          <Card className="mt-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5" />
                Your Trend Profile
                <Badge variant="outline" className="capitalize">
                  {trendProfile.profile_strength} profile
                </Badge>
              </CardTitle>
              <CardDescription>
                Based on {trendProfile.total_interactions || 0} interactions over {trendProfile.lookback_days || 30} days
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {trendProfile.top_categories?.slice(0, 4).map((cat, idx) => {
                  const catData = trendProfile.categories?.[cat] || {};
                  return (
                    <div key={cat} className="p-3 bg-gray-50 rounded-lg text-center">
                      <div className="text-xs text-gray-500 mb-1">#{idx + 1}</div>
                      <div className="font-semibold text-sm">{cat}</div>
                      <div className="text-lg font-bold text-purple-600">{catData.score?.toFixed(0) || 0}</div>
                      <div className="flex items-center justify-center gap-1 mt-1">
                        {catData.trend === 'rising' && <ChevronUp className="w-3 h-3 text-green-500" />}
                        {catData.trend === 'declining' && <ChevronDown className="w-3 h-3 text-red-500" />}
                        {catData.trend === 'stable' && <Minus className="w-3 h-3 text-gray-400" />}
                        <span className="text-xs text-gray-500 capitalize">{catData.trend || 'stable'}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Potential Earnings Section (Affiliate) */}
        <Card className="mt-6 border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <Wallet className="w-5 h-5 text-green-600" />
                Potential Earnings
                <Badge className="bg-green-600 text-white text-xs">
                  <DollarSign className="w-3 h-3 mr-1" />
                  Affiliate
                </Badge>
              </CardTitle>
              <span className="text-xs text-gray-500">
                Based on 30-day engagement
              </span>
            </div>
            <CardDescription>
              Estimated revenue from your trend engagement patterns
            </CardDescription>
          </CardHeader>
          <CardContent>
            {potentialEarnings ? (
              <div className="space-y-4">
                {/* Earnings Summary Cards */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm border border-green-100">
                    <div className="text-2xl font-bold text-green-600">
                      ${potentialEarnings.total_estimated_earnings?.toFixed(2) || '0.00'}
                    </div>
                    <div className="text-xs text-gray-500">Est. This Period</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm border border-green-100">
                    <div className="text-2xl font-bold text-emerald-600">
                      ${potentialEarnings.monthly_projection?.toFixed(2) || '0.00'}
                    </div>
                    <div className="text-xs text-gray-500">Monthly Projection</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-lg shadow-sm border border-green-100">
                    <div className="text-2xl font-bold text-teal-600">
                      ${potentialEarnings.yearly_projection?.toFixed(2) || '0.00'}
                    </div>
                    <div className="text-xs text-gray-500">Yearly Projection</div>
                  </div>
                </div>

                {/* Top Categories by Earnings */}
                {potentialEarnings.top_categories && potentialEarnings.top_categories.length > 0 && (
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-3">
                      <ShoppingBag className="w-4 h-4 text-green-600" />
                      <span className="font-semibold text-sm">Top Earning Categories</span>
                    </div>
                    <div className="space-y-2">
                      {potentialEarnings.top_categories.slice(0, 5).map((cat, idx) => (
                        <div 
                          key={cat.category} 
                          className="flex items-center justify-between p-3 bg-white rounded-lg border border-green-100"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center text-xs font-bold text-green-700">
                              {idx + 1}
                            </div>
                            <div>
                              <div className="font-medium text-sm">{cat.category}</div>
                              <div className="text-xs text-gray-500">
                                {cat.provider} • {cat.interactions} interactions
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-green-600">
                              ${cat.estimated_commission?.toFixed(2)}
                            </div>
                            <div className="text-xs text-gray-500">
                              ~{cat.estimated_clicks?.toFixed(0)} clicks
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Disclaimer */}
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-yellow-700">
                      {potentialEarnings.disclaimer || "These are estimated projections based on engagement patterns. Actual earnings depend on conversions."}
                    </p>
                  </div>
                </div>

                {/* CTA Button */}
                <div className="mt-4 text-center">
                  <Link 
                    to="/affiliate"
                    className="inline-flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-full hover:shadow-lg transition-all"
                  >
                    <DollarSign className="w-4 h-4" />
                    View Affiliate Dashboard
                    <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Wallet className="w-12 h-12 mx-auto text-green-300 mb-3" />
                <p className="text-green-700 font-medium">Start Engaging to Earn</p>
                <p className="text-xs text-green-500 mt-1">
                  Click on trend marketplace links to track your potential earnings
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
